import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';

interface SubscriptionDetails {
  id: string;
  status: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  productName: string;
  productId: string;
}

/**
 * A banner component that shows active subscription information
 */
const ActiveSubscriptionBanner: React.FC = () => {
  const { user, getToken, email } = useAuth();
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);
  const [loading, setLoading] = useState(true);

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Check for active subscription when the user is authenticated
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      // Wait for user and email from auth context
      if (!user || !email) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        // Debug logging: show user and email before subscription check
        console.log("[ActiveSubscriptionBanner] user:", user);
        console.log("[ActiveSubscriptionBanner] email:", email);
        const token = await getToken();
        console.log("[ActiveSubscriptionBanner] Bearer token:", token);
        // GET with email in query string to ensure correct lookup
        const url = `/api/check-subscription?email=${encodeURIComponent(email)}`;
        console.log("[ActiveSubscriptionBanner] Fetching subscription status with GET query", { url, headers: { Authorization: 'Bearer [REDACTED]' } });
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          }
        });
        console.log("[ActiveSubscriptionBanner] response status:", response.status, response.statusText);
        if (!response.ok) {
          console.error('Failed to check subscription status:', await response.text());
          setLoading(false);
          return;
        }
        const data = await response.json();
        setHasActiveSubscription(data.hasActiveSubscription);
        setSubscriptionDetails(data.subscription);
      } catch (err) {
        console.error('Error checking subscription status:', err);
      } finally {
        setLoading(false);
      }
    };
    
    checkSubscriptionStatus();
  // Rerun when user or email changes to ensure email is available
  }, [user, email]);

  if (loading || !user || !hasActiveSubscription || !subscriptionDetails) {
    return null; // Don't render anything if not applicable
  }

  return (
    <div className="bg-blue-50 border-b border-blue-100">
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between text-sm">
        <div className="flex items-center">
          <Check className="h-4 w-4 mr-2 text-blue-600" /> 
          <span>
            <span className="font-semibold">{subscriptionDetails.productName}</span> subscription active.
          </span>
        </div>
        <Link to="/subscription" className="text-blue-600 hover:text-blue-800 underline text-sm">
          Manage
        </Link>
      </div>
    </div>
  );
};

export default ActiveSubscriptionBanner;